import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Form as FormikForm, Formik } from 'formik';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { mortgageLookups } from '@ourbranch/lookups';

import { useStore } from 'core/store';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { Label } from 'core/components/label';
import ValueField from 'core/components/value-field';
import { FormField } from 'core/components/form';
import { AuthContext } from 'core/components/auth';
import { useScroll } from 'core/hooks/use-scroll';
import { TooltipWrapper } from 'core/components/tooltip-wrapper';
import AddressAutocomplete from 'core/components/address-autocomplete';
import { getMatchingLenders, trackCustomMortgageLender } from 'offer/helpers/mortgage-helpers';
import { IncorrectBillingWarning } from '../incorrect-billing-warning';
import { mortgageValidationSchema } from '../mortgage-details.validation-schema';
import { CustomLenderModal } from '../custom-lender-modal';
import useStyles from './add-mortgage.styles';

const initialValues = {
  loanNumber: '',
  mortgageHolderAddress: {
    zip: '',
    address: '',
    city: '',
    state: ''
  },
  mortgageHolderName: '',
  primary: false,
  isCustom: false,
  // Farm only: which structure this mortgage applies to (locationId::riskId). (#32526)
  appliesToStructure: ''
};

const addressTooltip = {
  label: 'More info',
  text: "Enter the lender's billing address. This may differ from the local branch's physical address. If you're unsure, contact the lender to confirm the correct address for escrow-billed  policy documents."
};

const addressLine2Tooltip = {
  label: 'More info',
  text: 'Apt, unit, floor, building, etc., are secondary address designators, (AKA Address line 2), that help ensure we have the right additional address details. Typically optional, but can be required if we find that the address may have it.'
};

const emailTooltipText =
  "Adding the lender's email can streamline billing processing by providing a backup method for payment collection. While optional, it is highly recommended.";

const faxTooltipText =
  "Adding the lender's fax number can streamline billing processing by providing a backup method for payment collection. While optional, it is highly recommended.";

const mortgageList = mortgageLookups?.map((mortgage, index) => ({
  id: index,
  text: mortgage.correctName
}));

const AddMortgage = ({ disabled, onAdd, structureOptions }) => {
  const classes = useStyles();
  const { viewOnly } = useContext(AuthContext);
  const {
    account: {
      policies: { policy: policyStore }
    },
    offer
  } = useStore();
  const { inOfferEditRoute } = useRouteHelper();

  const { policy } = policyStore;
  const [showModal, setShowModal] = useState(false);
  const [existingMatchingLenders, setExistingMatchingLenders] = useState([]);
  const idToTrack = policy?.id ? { policyId: policy?.id } : { offerId: offer?.offer?.id };

  useScroll(offer?.scrollTo || policyStore?.scrollTo);

  const addressToUse = policy?.id
    ? `${policy?.offer?.quote?.correctedAddress?.address} ${policy?.offer?.quote?.correctedAddress?.city}, ${policy?.offer?.quote?.correctedAddress?.state}, ${policy?.offer?.quote?.correctedAddress?.zip}`
    : `${offer?.offer?.quote?.correctedAddress?.address} ${offer?.offer?.quote?.correctedAddress?.city}, ${offer?.offer?.quote?.correctedAddress?.state}, ${offer?.offer?.quote?.correctedAddress?.zip}`;

  return (
    <Formik
      // "Applies to structure" starts empty ("Select") and is required for farm mortgages — an agent
      // must consciously choose which structure the lender covers rather than silently defaulting to
      // the first one on the policy (the old default mis-associated multi-structure farms). (#32664)
      initialValues={initialValues}
      validationSchema={() =>
        mortgageValidationSchema({ inOfferEditRoute, requireAppliesToStructure: structureOptions?.length > 0 })
      }
      validateOnChange={false}
    >
      {({ setFieldValue, setFieldTouched, values, validateForm, setErrors, setTouched }) => {
        const existingMortgageLenderSelected =
          values.mortgageHolderName && values.mortgageHolderAddress?.address && !values.isCustom;

        return (
          <FormikForm className={classes.container}>
            <Grid
              id="addMortgage"
              key="container"
              container
              justifyContent="space-around"
              alignItems="flex-start"
              spacing={4}
            >
              <Label className={classes.addLabel} type="greenSmall">
                Add mortgage lender
              </Label>
              <FormField
                type="mortgageLenderName"
                mode="light"
                name="mortgageHolderName"
                id="mortgageHolderName"
                options={mortgageList}
                onSelection={(_, mortgageLender) => {
                  if (!mortgageLender) {
                    return;
                  }

                  const mortgage = mortgageLookups.find(({ correctName }) => correctName === mortgageLender.text);
                  if (mortgage) {
                    const { address, address2, city, state, zip, id: mortgageID } = mortgage;
                    setFieldValue('mortgageHolderAddress', { address, address2, city, state, zip });
                    setFieldValue('id', mortgageID || mortgageLender.id);
                    setFieldTouched('mortgageHolderAddress', true, false);
                  }
                }}
                onCustomSelection={() => {
                  setFieldValue('mortgageHolderAddress', { address: '', address2: '', city: '', state: '', zip: '' });
                  setFieldValue('loanNumber', '');
                }}
                label="Lender Search"
                helperText="Search for a lender in our database"
                xs={12}
                permissions={{ isLicensedAction: false }}
                showChevronIcon
                showDeleteIcon
              />

              {/* Farm only: associate this mortgage with a specific structure. (#32526) A disabled
                  "Select…" placeholder makes the required-but-empty initial state explicit. (#32664) */}
              {structureOptions?.length > 0 && (
                <FormField
                  mode="light"
                  type="select"
                  name="appliesToStructure"
                  id="appliesToStructure"
                  label="Applies to structure"
                  options={[{ id: '', value: 'Select a structure', disabled: true }, ...structureOptions]}
                  xs={12}
                  fast={false}
                  permissions={{ isLicensedAction: false }}
                />
              )}

              {existingMortgageLenderSelected && (
                <>
                  <Grid container item xs={12} spacing={2}>
                    <ValueField
                      label="Billing Address"
                      showTooltip={{
                        label: 'More info',
                        onHoverText:
                          "This address has been verified for billing this lender. It may differ from the physical address of the lender's local branch. If you believe this is incorrect, please contact support."
                      }}
                      value={`${values.mortgageHolderAddress?.address} ${values.mortgageHolderAddress?.city}, ${values.mortgageHolderAddress?.state}, ${values.mortgageHolderAddress?.zip}`}
                    />
                  </Grid>
                  <FormField
                    mode="light"
                    name="loanNumber"
                    id="loanNumber"
                    label="Loan Number"
                    type="string"
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                </>
              )}
              {values.isCustom && (
                <>
                  <AddressAutocomplete
                    mode="light"
                    key={values.id}
                    name="mortgageHolderAddress.address"
                    id="mortgageHolderAddress.address"
                    type="string"
                    label="Billing Address"
                    xs={7}
                    tooltip={addressTooltip}
                    permissions={{ isLicensedAction: false }}
                  />
                  <TooltipWrapper xs={5} tooltip={addressLine2Tooltip}>
                    <FormField
                      mode="light"
                      name="mortgageHolderAddress.address2"
                      id="mortgageHolderAddress.address2"
                      label="Apt, unit, floor, building, etc."
                      type="string"
                      optional
                      permissions={{ isLicensedAction: false }}
                    />
                  </TooltipWrapper>
                  <Grid item xs={4}>
                    <FormField
                      mode="light"
                      name="mortgageHolderAddress.city"
                      id="mortgageHolderAddress.city"
                      label="City"
                      type="string"
                      permissions={{ isLicensedAction: false }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormField
                      item
                      mode="light"
                      name="mortgageHolderAddress.state"
                      id="mortgageHolderAddress.state"
                      label="State"
                      type="state"
                      permissions={{ isLicensedAction: false }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormField
                      mode="light"
                      name="mortgageHolderAddress.zip"
                      id="mortgageHolderAddress.zip"
                      label="Zip Code"
                      type="string"
                      permissions={{ isLicensedAction: false }}
                    />
                  </Grid>
                  <IncorrectBillingWarning />
                  <TooltipWrapper xs={6} tooltip={{ text: emailTooltipText }}>
                    <FormField
                      mode="light"
                      name="mortgageHolderEmail"
                      id="mortgageHolderEmail"
                      label="Email (optional)"
                      type="string"
                      permissions={{ isLicensedAction: false }}
                    />
                  </TooltipWrapper>
                  <TooltipWrapper xs={6} tooltip={{ text: faxTooltipText }}>
                    <FormField
                      mode="light"
                      name="mortgageHolderFax"
                      id="mortgageHolderFax"
                      label="Fax (optional)"
                      type="string"
                      permissions={{ isLicensedAction: false }}
                    />
                  </TooltipWrapper>
                  <Grid item xs={12}>
                    <FormField
                      mode="light"
                      name="loanNumber"
                      id="loanNumber"
                      label="Loan Number"
                      type="string"
                      permissions={{ isLicensedAction: false }}
                    />
                  </Grid>
                </>
              )}
              <Grid container className={classes.buttonContainer}>
                <Button
                  key="addButton"
                  type="submit"
                  mode="big"
                  variant="contained"
                  color="secondary"
                  disabled={disabled || viewOnly}
                  onClick={() => {
                    validateForm().then((errors) => {
                      if (Object.keys(errors).length) {
                        setErrors(errors);
                        setTouched(errors);
                        return;
                      }
                      if (values.isCustom) {
                        const customLenderAddress = `${values.mortgageHolderAddress.address}, ${values.mortgageHolderAddress.city}, ${values.mortgageHolderAddress.state}, ${values.mortgageHolderAddress.zip}`;
                        const matchingLenders = getMatchingLenders(values.mortgageHolderName, customLenderAddress);

                        if (matchingLenders.length > 0) {
                          setExistingMatchingLenders(matchingLenders);
                          setShowModal(true);
                        } else {
                          trackCustomMortgageLender(true, idToTrack, values);
                          onAdd(values);
                        }
                      } else {
                        onAdd(values);
                      }
                    });
                  }}
                >
                  Add lender
                </Button>
              </Grid>
            </Grid>
            {showModal && (
              <CustomLenderModal
                open={showModal}
                onClose={() => setShowModal(false)}
                onConfirm={async (selectedLender) => {
                  if (selectedLender.isCustom) {
                    trackCustomMortgageLender(true, idToTrack, values);
                    onAdd(values);
                  } else {
                    const {
                      address,
                      address2,
                      city,
                      state,
                      zip,
                      id: mortgageID,
                      correctName
                    } = existingMatchingLenders[selectedLender.index];
                    const existingLenderValues = {
                      isCustom: false,
                      mortgageHolderName: correctName,
                      mortgageHolderAddress: { address, address2, city, state, zip },
                      id: mortgageID,
                      loanNumber: values.loanNumber,
                      primary: false,
                      // Preserve the farm structure association through the matching-lender modal. (#32526)
                      appliesToStructure: values.appliesToStructure
                    };
                    onAdd(existingLenderValues);
                  }
                }}
                existingMortgages={existingMatchingLenders}
                customMortgage={{
                  correctName: values.mortgageHolderName,
                  ...values.mortgageHolderAddress
                }}
                address={addressToUse}
              />
            )}
          </FormikForm>
        );
      }}
    </Formik>
  );
};
AddMortgage.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
  // Farm only: "Applies to structure" dropdown options (locationId::riskId). (#32526)
  structureOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, value: PropTypes.string }))
};

AddMortgage.defaultProps = {
  structureOptions: undefined
};

export default AddMortgage;
